import * as React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { USER_ROLES } from '@/constants/user-roles';
import { hasAnyAccountRoles, hasAnyRolesV2, hasCommunityRolesV2 } from '@/utils/has-roles';
import { Avatar, Button, Divider } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowSquareOut as ArrowSquareOutIcon } from '@phosphor-icons/react/dist/ssr/ArrowSquareOut';
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';

import type { Account } from '@/types/v2/account';
import { AccountType } from '@/types/v2/account';
import type { Community } from '@/types/v2/community';
import type { NavItemConfig } from '@/types/v2/nav';
import type { NavColor } from '@/types/v2/settings';
import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { isNavItemActive } from '@/lib/v2/is-nav-item-active';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { usePathname } from '@/hooks/v2/use-pathname';
import { usePopover } from '@/hooks/v2/use-popover';
import { useSettings } from '@/hooks/v2/use-settings';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { RouterLink } from '@/components/core/v2/link';
import { Logo } from '@/components/core/v2/logo';
import { UserAvatar } from '@/components/common/user-avatar';
import type { ColorScheme } from '@/styles/theme-v2/theme/types';

import { CommunitiesSwitch } from './communities-switch';
import { icons } from './nav-icons';
import { UserPopover } from './user-popover/user-popover';
import { navColorStyles } from './vertical/styles';

const logoColors = {
  dark: { blend_in: 'light', discrete: 'light', evident: 'light' },
  light: { blend_in: 'dark', discrete: 'dark', evident: 'dark' },
} as Record<ColorScheme, Record<NavColor, 'dark' | 'light'>>;

export interface MobileNavProps {
  onClose?: () => void;
  open?: boolean;
  items?: NavItemConfig[];
  bottomItems?: NavItemConfig[];
  user?: User;
  community?: Community;
  account?: Account;
}

export function MobileNav({ items = [], bottomItems = [], open, onClose }: MobileNavProps): React.JSX.Element {
  const pathname = usePathname();
  const popover = usePopover<HTMLButtonElement>();
  const color = 'discrete';
  const { user, account, community } = useUser();
  const {
    settings: { colorScheme = 'light' },
  } = useSettings();
  const { isFeatureEnabled } = useFeatureFlags();
  const styles = navColorStyles[colorScheme][color];
  const logoColor = logoColors[colorScheme][color];
  return (
    <Drawer
      PaperProps={{
        sx: {
          '--MobileNav-background': styles['--SideNav-background'],
          '--MobileNav-color': styles['--SideNav-color'],
          '--NavGroup-title-color': styles['--NavGroup-title-color'],
          '--NavItem-color': styles['--NavItem-color'],
          '--NavItem-hover-background': styles['--NavItem-hover-background'],
          '--NavItem-active-background': styles['--NavItem-active-background'],
          '--NavItem-active-color': styles['--NavItem-active-color'],
          '--NavItem-disabled-color': styles['--NavItem-disabled-color'],
          '--NavItem-icon-color': styles['--NavItem-icon-color'],
          '--NavItem-icon-active-color': styles['--NavItem-icon-active-color'],
          '--NavItem-icon-disabled-color': 'var(--mui-palette-neutral-600)',
          '--NavItem-expand-color': styles['--NavItem-expand-color'],
          '--NavItem-children-border': styles['--NavItem-children-border'],
          '--NavItem-children-indicator': styles['--NavItem-children-indicator'],
          '--Workspaces-background': styles['--Workspaces-background'],
          '--Workspaces-border-color': styles['--Workspaces-border-color'],
          '--Workspaces-title-color': styles['--Workspaces-title-color'],
          '--Workspaces-name-color': styles['--Workspaces-name-color'],
          '--Workspaces-expand-color': styles['--Workspaces-expand-color'],
          bgcolor: styles['--SideNav-background'],
          color: styles['--SideNav-color'],
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100%',
          width: 'var(--MobileNav-width)',
          zIndex: 'var(--MobileNav-zIndex)',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        },
      }}
      onClose={onClose}
      open={open}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        <div>
          <Box component={RouterLink} href={paths.home} sx={{ display: 'inline-flex' }}>
            <Logo color={logoColor} height={40} />
          </Box>
        </div>
        {/* Enable the CommunitiesSwitch only for managed accounts */}

        {account?.type === AccountType.MANAGED &&
        community &&
        user &&
        (hasCommunityRolesV2(community, [COMMUNITY_ROLES.COMMUNITY_MANAGER]) ||
          hasAnyAccountRoles(account, [ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER])) ? (
          <RoleGuard
            requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <CommunitiesSwitch />
          </RoleGuard>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              border: '1px solid var(--Workspaces-border-color)',
              borderRadius: '12px',
              px: 2,
              py: 1,
              boxShadow: (theme) => theme.shadows[1],
              background: (theme) => theme.palette.background.paper,
            }}
          >
            <Avatar src={community?.logo_url} variant="square">
              {community?.name
                .split(' ')
                .map((name: string) => name.charAt(0))
                .join('')
                .toUpperCase()}
            </Avatar>
            <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
              <Typography color="var(--Workspaces-title-color)" variant="caption">
                Your Community
              </Typography>
              <Typography
                color="var(--Workspaces-name-color)"
                sx={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
                variant="subtitle2"
              >
                {community?.name}
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>
      <Box component="nav" sx={{ flex: '1 1 auto', p: 2 }}>
        {renderNavGroups({
          items,
          onClose,
          pathname,
          user: user ?? undefined,
          community: community ?? undefined,
          account: account ?? undefined,
          isFeatureEnabled,
        })}
      </Box>
      <Box component="nav" sx={{ p: 2 }}>
        {renderNavGroups({
          items: bottomItems,
          onClose,
          pathname,
          account: account ?? undefined,
          user: user ?? undefined,
          community: community ?? undefined,
          isFeatureEnabled,
        })}
      </Box>
      <Divider />
      <Box alignItems="center" display="flex" gap={1} sx={{ p: 2 }}>
        <Button
          color="inherit"
          onClick={popover.handleOpen}
          ref={popover.anchorRef}
          startIcon={<UserAvatar user={user} size="32px" />}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textTransform: 'none',
            padding: '10px 16px',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'var(--NavItem-hover-background)',
              color: 'var(--NavItem-hover-color)',
            },
          }}
          variant="contained"
        >
          <Stack alignItems="flex-start">
            <Typography variant="body2">
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="body2">{user?.email}</Typography>
          </Stack>
        </Button>
        <UserPopover anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
      </Box>
    </Drawer>
  );
}

function renderNavGroups({
  items,
  onClose,
  pathname,
  user,
  community,
  account,
  isFeatureEnabled,
}: {
  items: NavItemConfig[];
  onClose?: () => void;
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
  isFeatureEnabled: (flag: string) => boolean;
}): React.JSX.Element {
  const children = items.reduce((acc: React.ReactNode[], curr: NavItemConfig): React.ReactNode[] => {
    const { items: childItems, key, roles, communityRoles, accountRoles, ...item } = curr;
    const flags =
      typeof item.featureFlags === 'string'
        ? item.featureFlags.split(',')
        : Array.isArray(item.featureFlags)
          ? item.featureFlags
          : [];

    if (flags.length > 0) {
      const isEnabled = flags.some((flag) => isFeatureEnabled(flag));
      if (!isEnabled) return acc;
    }

    let itemHasAccess = true; // Default to true if no roles specified

    if (roles || accountRoles || communityRoles) {
      const itemHasAccountAccess = (roles || accountRoles)
        ? (roles || accountRoles)?.some((role) => account?.roles?.includes(role as unknown as any))
        : false;
      const itemHasCommunityAccess = communityRoles
        ? communityRoles.some((role) => community?.roles?.includes(role as unknown as any))
        : false;
      itemHasAccess = itemHasAccountAccess || itemHasCommunityAccess;
    }

    if (!itemHasAccess) return acc; // Skip if user doesn't have access

    acc.push(
      <Stack component="li" key={curr.key} spacing={1.5}>
        {curr.title && curr.items && curr.items.length > 0 ? (
          <div>
            <Typography sx={{ color: 'var(--NavGroup-title-color)', fontSize: '0.875rem', fontWeight: 500 }}>
              {curr.title}
            </Typography>
          </div>
        ) : null}
        <div>
          {renderNavItems({
            depth: 0,
            items: curr.items,
            onClose,
            pathname,
            user,
            account,
            community,
            isFeatureEnabled,
          })}
        </div>
      </Stack>
    );
    return acc;
  }, []);

  return (
    <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}

function renderNavItems({
  depth = 0,
  items = [],
  onClose,
  pathname,
  user,
  community,
  account,
  isFeatureEnabled,
}: {
  depth: number;
  items?: NavItemConfig[];
  onClose?: () => void;
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
  isFeatureEnabled: (flag: string) => boolean;
}): React.JSX.Element {
  const children = items.reduce((acc: React.ReactNode[], curr: NavItemConfig): React.ReactNode[] => {
    const { items: childItems, key, roles, communityRoles, accountRoles, ...item } = curr;

    const flags =
      typeof item.featureFlags === 'string'
        ? item.featureFlags.split(',')
        : Array.isArray(item.featureFlags)
          ? item.featureFlags
          : [];

    if (flags.length > 0) {
      const isEnabled = flags.some((flag) => isFeatureEnabled(flag));
      if (!isEnabled) return acc;
    }

    // Update the RBAC check to combine "roles" with "accountRoles"
    let itemHasAccess = true;
    if (roles || accountRoles || communityRoles) {
      const itemHasAccountAccess =
        roles || accountRoles
          ? (roles || accountRoles)?.some((role) => account?.roles?.includes(role as unknown as any))
          : false;
      const itemHasCommunityAccess = communityRoles
        ? communityRoles.some((role) => community?.roles?.includes(role as unknown as any))
        : false;
      itemHasAccess = itemHasAccountAccess || itemHasCommunityAccess;
    }

    if (!itemHasAccess) return acc;

    const forceOpen = childItems
      ? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
      : false;

    acc.push(
      <NavItem
        depth={depth}
        forceOpen={forceOpen}
        key={key}
        onClose={onClose}
        pathname={pathname}
        user={user}
        {...item}
      >
        {childItems
          ? renderNavItems({
              depth: depth + 1,
              items: childItems,
              onClose,
              pathname,
              user,
              community,
              account,
              isFeatureEnabled,
            })
          : null}
      </NavItem>
    );
    return acc;
  }, []);

  return (
    <Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}

interface NavItemProps extends Omit<NavItemConfig, 'items'> {
  children?: React.ReactNode;
  depth: number;
  forceOpen?: boolean;
  onClose?: () => void;
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
}

function NavItem({
  children,
  depth,
  disabled,
  external,
  forceOpen = false,
  href,
  icon,
  label,
  matcher,
  onClose,
  pathname,
  title,
}: NavItemProps): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(forceOpen);
  const active = isNavItemActive({ disabled, external, href: href ?? '', matcher, pathname });
  const Icon = icon ? icons[icon] : null;
  const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;
  const isBranch = Boolean(children && !href);
  const showChildren = Boolean(children && open);

  return (
    <Box component="li" data-depth={depth} sx={{ userSelect: 'none' }}>
      <Box
        {...(isBranch
          ? {
              onClick: (): void => {
                setOpen(!open);
              },
              onKeyUp: (event: React.KeyboardEvent<HTMLElement>): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setOpen(!open);
                }
              },
              role: 'button',
            }
          : {
              ...(href && !disabled
                ? {
                    component: external ? 'a' : RouterLink,
                    href,
                    target: external ? '_blank' : undefined,
                    rel: external ? 'noreferrer' : undefined,
                    onClick: (): void => {
                      onClose?.();
                    },
                  }
                : { role: 'button' }),
            })}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'var(--NavItem-color)',
          cursor: 'pointer',
          display: 'flex',
          flex: '0 0 auto',
          gap: 1,
          p: '6px 16px',
          position: 'relative',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          ...(disabled && {
            bgcolor: 'var(--NavItem-disabled-background)',
            color: 'var(--NavItem-disabled-color)',
            cursor: 'not-allowed',
          }),
          ...(active && {
            bgcolor: 'var(--NavItem-active-background)',
            color: 'var(--NavItem-active-color)',
            ...(depth > 0 && {
              '&::before': {
                bgcolor: 'var(--NavItem-children-indicator)',
                borderRadius: '2px',
                content: '" "',
                height: '20px',
                left: '-14px',
                position: 'absolute',
                width: '3px',
              },
            }),
          }),
          ...(open && { color: 'var(--NavItem-open-color)' }),
          '&:hover': {
            ...(!disabled &&
              !active && { bgcolor: 'var(--NavItem-hover-background)', color: 'var(--NavItem-hover-color)' }),
          },
        }}
        tabIndex={0}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
          {Icon ? (
            <Icon
              fill={active ? 'var(--NavItem-icon-active-color)' : 'var(--NavItem-icon-color)'}
              fontSize="var(--icon-fontSize-md)"
              weight={forceOpen || active ? 'fill' : undefined}
            />
          ) : null}
        </Box>
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
          >
            {title}
          </Typography>
        </Box>
        {label ? <Chip color="primary" label={label} size="small" /> : null}
        {external ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ArrowSquareOutIcon color="var(--NavItem-icon-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
        {isBranch ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
      </Box>
      {showChildren ? (
        <Box sx={{ pl: '24px' }}>
          <Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
        </Box>
      ) : null}
    </Box>
  );
}
